/**
 * AA — Build a Subscription
 * Client-side subscription "box" builder. Items live in localStorage and are
 * isolated from the real Shopify cart until checkout, when they are pushed to
 * /cart/add.js (with per-product selling plans) and the shopper is redirected
 * to /checkout. Products are subscription-only; one global frequency selector
 * resolves each product to its own matching Shopify selling plan.
 */
(function () {
  'use strict';

  var LOG = '[BAS]'; // Build-a-Subscription debug prefix
  console.log(LOG, 'script file executed');

  // Curated-box "What's Inside" tab switcher (used by inline onclick in the card).
  window.switchBundleVariant = function (button, index) {
    var details = button.closest('.bundle-items-details');
    if (!details) return;
    details.querySelectorAll('.variant-tab').forEach(function (tab) {
      tab.classList.remove('active');
    });
    button.classList.add('active');
    details.querySelectorAll('.variant-bundle-content').forEach(function (content) {
      content.classList.remove('active');
    });
    var selected = details.querySelector('.variant-bundle-content[data-variant-index="' + index + '"]');
    if (selected) selected.classList.add('active');
  };

  // The theme's cart trash icon (snippets/icon-remove.liquid), inlined for JS rendering.
  var REMOVE_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false" class="icon icon-remove">' +
    '<path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 00.5-.5V4H14a.5.5 0 000-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"/>' +
    '<path d="M6.55 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5zM9.45 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z" fill="currentColor"/>' +
    '</svg>';

  /* ----------------------------- money helper ---------------------------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = format || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    var placeholder = match ? match[1] : 'amount';
    var value;
    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ----------------------------- controller ------------------------------ */
  // Plain class bound to the <aa-subscription-box> element. NOT a custom
  // element — instantiated directly on DOMContentLoaded so it never depends on
  // custom-element upgrade timing (which was preventing listeners from binding).
  class SubscriptionBox {
    constructor(root) {
      console.log(LOG, 'constructor: wiring controller to', root);
      this.root = root;
      // Delegate DOM lookups/events to the root element so existing this.* calls work.
      this.querySelector = root.querySelector.bind(root);
      this.querySelectorAll = root.querySelectorAll.bind(root);
      this.addEventListener = root.addEventListener.bind(root);
      this.dataset = root.dataset;
      this.init();
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.threshold = parseInt(this.dataset.threshold, 10) || 18900;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.showProtection = this.dataset.showProtection === 'true';
      this.protectionVariant = (this.dataset.protectionVariant || '').trim();
      this.storageKey = 'aa-bas-' + this.sectionId;

      this.products = {}; // productId -> product data
      this.variants = {}; // variantId -> variant data (+ productId/title/image)

      this.indexProducts();

      this.state = this.loadState();
      this.pruneStaleItems();

      this.indexProductsLogged = Object.keys(this.products).length;

      this.cacheEls();

      // Move the modal to <body> so its position:fixed is viewport-relative and
      // the dim overlay covers the whole screen, even if an ancestor section
      // establishes a containing block (transform/filter/etc.).
      if (this.modalEl && this.modalEl.parentNode !== document.body) {
        document.body.appendChild(this.modalEl);
      }

      console.log(
        LOG,
        'init done. products indexed:', this.indexProductsLogged,
        '| box found:', !!this.boxEl,
        '| items region:', !!this.itemsEl,
        '| frequency select:', !!this.frequencySelect,
        '| add buttons on page:', this.querySelectorAll('[data-add-to-box]').length
      );
      this.bindEvents();
      console.log(LOG, 'click + change listeners bound to root element');
      this.logPlanDiagnostics();

      // Rendering must never prevent event handlers (already bound above) from working.
      try {
        this.renderCardPrices();
        this.render();
        this.initCarousels();
        this.initNav();
        this.trackBoxHeight();
      } catch (e) {
        console.error(LOG, 'render error', e);
      }
    }

    // On mobile the box is fixed to the bottom of the viewport, so it covers the
    // end of the page. Publish its height as --bas-box-height on <html>; the CSS
    // uses it to clear the footer. Measured only while the items list is CLOSED,
    // so opening the panel doesn't yank the footer margin around with it.
    trackBoxHeight() {
      if (!this.boxEl) return;

      var measure = () => {
        if (this.boxEl.classList.contains('bas-box--items-open')) return;
        // An empty bar is hidden, so the footer needs no clearance. Otherwise a
        // zero height means "not laid out yet" (or temporarily hidden behind the
        // theme's menu drawer) — keep the last good measurement.
        var isEmpty = this.boxEl.classList.contains('bas-box--empty');
        var h = isEmpty ? 0 : this.boxEl.offsetHeight;
        if (!isEmpty && !h) return;
        if (h === this.lastBoxHeight) return;
        this.lastBoxHeight = h;
        document.documentElement.style.setProperty('--bas-box-height', h + 'px');
      };

      // Exposed so an add can re-measure the just-revealed bar *before* it opens
      // (measure() no-ops while the cart view is open).
      this.measureBoxHeight = measure;

      measure();
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(measure).observe(this.boxEl);
      }
      window.addEventListener('resize', measure);
      window.addEventListener('orientationchange', measure);
    }

    /* ------------------------- data indexing ---------------------------- */
    indexProducts() {
      var scripts = this.querySelectorAll('.bas-product-data');
      scripts.forEach((script) => {
        var data;
        try {
          data = JSON.parse(script.textContent);
        } catch (e) {
          console.error(LOG, 'indexProducts JSON parse FAILED:', e.message, '\nraw:', script.textContent);
          return;
        }
        this.products[data.id] = data;
        (data.variants || []).forEach((v) => {
          this.variants[v.id] = Object.assign({}, v, {
            productId: data.id,
            productTitle: data.title,
            productImage: v.image || data.featured_image,
          });
        });
      });
    }

    // Diagnostic: lists the frequency options and which product/plans feed them.
    // The dropdown = the union of every distinct selling-plan NAME across the
    // rendered products. Extra options mean products are enrolled in extra
    // selling plans (usually more than one selling-plan group in the app).
    logPlanDiagnostics() {
      var self = this;
      if (this.frequencySelect) {
        var opts = Array.prototype.map.call(this.frequencySelect.options, function (o) {
          return o.value;
        });
        console.log(LOG, 'FREQUENCY OPTIONS (' + opts.length + '):', opts);
      }
      Object.keys(this.products).forEach(function (pid) {
        var p = self.products[pid];
        var names = {};
        (p.variants || []).forEach(function (v) {
          Object.keys(v.plans || {}).forEach(function (n) {
            names[n] = true;
          });
        });
        console.log(LOG, 'product "' + p.title + '" (' + pid + ') selling plans:', Object.keys(names));
      });
    }

    cacheEls() {
      this.boxEl = this.querySelector('[data-box]');
      this.itemsEl = this.querySelector('[data-box-items]');
      this.totalPriceEl = this.querySelector('[data-total-price]');
      this.totalCompareEl = this.querySelector('[data-total-compare]');
      this.mobileTotalEl = this.querySelector('[data-mobile-total]');
      this.shippingMsgEl = this.querySelector('[data-shipping-message]');
      this.shippingFillEl = this.querySelector('[data-shipping-fill]');
      this.checkoutBtn = this.querySelector('[data-checkout]');
      this.checkoutPriceEl = this.querySelector('[data-checkout-price]');
      this.checkoutNoProtectionBtn = this.querySelector('[data-checkout-without-protection]');
      this.cartCountEls = this.querySelectorAll('[data-cart-count]');
      this.errorEl = this.querySelector('[data-box-error]');
      this.frequencySelect = this.querySelector('[data-frequency]');
      this.modalEl = this.querySelector('[data-modal]');
      this.modalBodyEl = this.querySelector('[data-modal-body]');
    }

    /* ---------------------------- events -------------------------------- */
    bindEvents() {
      // Delegated clicks across the section. Attach FIRST so nothing below can
      // prevent it from binding.
      this.addEventListener('click', (e) => {
        console.log(LOG, 'click detected on', e.target);

        var openModal = e.target.closest('[data-open-modal]');
        if (openModal) {
          var card = openModal.closest('.bas-card');
          console.log(LOG, 'open-modal clicked, card:', card);
          if (card) this.openModal(parseInt(card.dataset.productId, 10));
          return;
        }

        var addBtn = e.target.closest('[data-add-to-box]');
        if (addBtn) {
          var addCard = addBtn.closest('.bas-card');
          console.log(LOG, 'ADD clicked. card:', addCard, 'productId:', addCard && addCard.dataset.productId);
          if (addCard) this.addFromCard(parseInt(addCard.dataset.productId, 10));
          return;
        }

        var itemsToggle = e.target.closest('[data-items-toggle]');
        if (itemsToggle) {
          this.setCartOpen(!this.boxEl.classList.contains('bas-box--items-open'));
          return;
        }

        if (e.target.closest('[data-modal-close]')) {
          this.closeModal();
          return;
        }

        // Box line item controls.
        var inc = e.target.closest('[data-line-inc]');
        if (inc) return this.changeQty(inc.dataset.variant, 1);
        var dec = e.target.closest('[data-line-dec]');
        if (dec) return this.changeQty(dec.dataset.variant, -1);
        var rm = e.target.closest('[data-line-remove]');
        if (rm) return this.removeItem(rm.dataset.variant);

        // Category nav pills.
        var pill = e.target.closest('[data-nav-pill]');
        if (pill) {
          var section = document.getElementById(pill.dataset.target);
          if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
          this.setActivePill(pill);
          return;
        }

        // Carousel arrows.
        var prev = e.target.closest('[data-carousel-prev]');
        if (prev) return this.scrollCarousel(prev.closest('[data-carousel]'), -1);
        var next = e.target.closest('[data-carousel-next]');
        if (next) return this.scrollCarousel(next.closest('[data-carousel]'), 1);

        // Checkout.
        // Main Checkout adds Shipping Protection (when enabled); the secondary
        // link checks out without it.
        var coBtn = e.target.closest('[data-checkout]');
        if (coBtn) return this.checkout(this.showProtection, coBtn);
        var coNoBtn = e.target.closest('[data-checkout-without-protection]');
        if (coNoBtn) return this.checkout(false, coNoBtn);

        // Modal interactions.
        if (this.modalProduct) this.handleModalClick(e);
      });

      // The modal is relocated to <body>, so its clicks no longer bubble to this
      // element — give it its own listener.
      if (this.modalEl) {
        this.modalEl.addEventListener('click', (e) => {
          if (e.target.closest('[data-modal-close]')) {
            this.closeModal();
            return;
          }
          if (this.modalProduct) this.handleModalClick(e);
        });

        // Disable the aa-pdp-thumbnails zoom for the MODAL carousel only (the
        // product pages keep it). Capture the featured-image click before the
        // component's zoom handler runs and stop it — but let the carousel
        // arrows, bullets and video overlay work.
        this.modalEl.addEventListener(
          'click',
          function (e) {
            if (e.target.closest('.carousel-arrow, .bullet-indicator, .video-play-overlay, [data-video-overlay]')) {
              return;
            }
            if (e.target.closest('.aa-pdp-featured')) {
              e.stopImmediatePropagation();
            }
          },
          true
        );
      }

      // Frequency change (global). Guarded so a missing select can't break init.
      if (this.frequencySelect) {
        this.frequencySelect.addEventListener('change', (e) => {
          this.state.frequency = e.target.value;
          this.saveState();
          this.renderCardPrices();
          this.render();
        });
      }

      // Close modal on ESC.
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modalEl && !this.modalEl.hidden) this.closeModal();
      });
    }

    /* ------------------------- state helpers ---------------------------- */
    loadState() {
      var fallback = {
        frequency: this.dataset.defaultFrequency || '',
        items: {},
      };
      try {
        var raw = localStorage.getItem(this.storageKey);
        if (!raw) return fallback;
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return fallback;
        parsed.items = parsed.items || {};
        if (!parsed.frequency) parsed.frequency = fallback.frequency;
        return parsed;
      } catch (e) {
        return fallback;
      }
    }

    saveState() {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch (e) {
        /* storage unavailable — continue in-memory */
      }
    }

    // Drop items whose variant is no longer rendered on the page.
    pruneStaleItems() {
      var self = this;
      Object.keys(this.state.items).forEach(function (variantId) {
        if (!self.variants[variantId]) delete self.state.items[variantId];
      });
      // Ensure the selected frequency still exists in the dropdown.
      var sel = this.querySelector('[data-frequency]');
      if (sel) {
        var names = Array.prototype.map.call(sel.options, function (o) {
          return o.value;
        });
        if (names.indexOf(this.state.frequency) === -1) {
          this.state.frequency = names[0] || this.state.frequency;
        }
        sel.value = this.state.frequency;
      }
    }

    /* --------------------- plan / price resolution ---------------------- */
    // Resolve a variant's plan for the current frequency (fallback: first plan).
    resolvePlan(variant) {
      if (!variant || !variant.plans) return null;
      var plan = variant.plans[this.state.frequency];
      if (plan) return plan;
      var keys = Object.keys(variant.plans);
      return keys.length ? variant.plans[keys[0]] : null;
    }

    /* ----------------------------- add ---------------------------------- */
    addFromCard(productId) {
      // The card's "Add to cart" always adds directly (first available variant),
      // matching the mockup. The modal (card click) is where a specific variant
      // can be chosen. Fall back to the card's own JSON if the index missed it.
      var product = this.products[productId] || this.readCardData(productId);
      console.log(LOG, 'addFromCard', productId, 'product resolved?', !!product);
      if (!product) {
        console.warn(LOG, 'no product data for', productId, '— nothing added');
        return;
      }
      var variants = product.variants || [];
      var variant = variants.find(function (v) {
        return v.available;
      }) || variants[0];
      console.log(LOG, 'adding variant', variant && variant.id);
      if (variant) this.addVariant(variant.id, 1);
    }

    // Fallback: re-read (and index) a product straight from its card script.
    readCardData(productId) {
      var card = this.querySelector('.bas-card[data-product-id="' + productId + '"]');
      var script = card && card.querySelector('.bas-product-data');
      if (!script) return null;
      var data;
      try {
        data = JSON.parse(script.textContent);
      } catch (e) {
        console.error(LOG, 'readCardData JSON parse FAILED:', e.message, '\nraw:', script.textContent);
        return null;
      }
      this.products[data.id] = data;
      var self = this;
      (data.variants || []).forEach(function (v) {
        self.variants[v.id] = Object.assign({}, v, {
          productId: data.id,
          productTitle: data.title,
          productImage: v.image || data.featured_image,
        });
      });
      return data;
    }

    addVariant(variantId, qty) {
      qty = qty || 1;
      var wasEmpty = Object.keys(this.state.items).length === 0;
      var item = this.state.items[variantId];
      if (item) {
        item.qty += qty;
      } else {
        var v = this.variants[variantId];
        if (!v) return;
        this.state.items[variantId] = {
          productId: v.productId,
          variantId: variantId,
          qty: qty,
        };
      }
      this.saveState();
      this.render();
      // First item into an empty box: reveal the bar already opened to the cart
      // view. Later adds leave whatever state the shopper had it in.
      if (wasEmpty) {
        if (this.measureBoxHeight) this.measureBoxHeight();
        if (this.isMobile()) this.setCartOpen(true);
      }
      this.flashBox();
    }

    changeQty(variantId, delta) {
      var item = this.state.items[variantId];
      if (!item) return;
      item.qty += delta;
      if (item.qty <= 0) {
        delete this.state.items[variantId];
      }
      this.saveState();
      this.render();
    }

    removeItem(variantId) {
      delete this.state.items[variantId];
      this.saveState();
      this.render();
    }

    isMobile() {
      return !window.matchMedia || window.matchMedia('(max-width: 749px)').matches;
    }

    // Mobile has three bar states: the open cart view, and — when closed — the
    // minimal bar or the free-shipping-unlocked bar (CSS picks between those two
    // off bas-box--free-shipping). This only flips the open/closed half.
    setCartOpen(open) {
      this.boxEl.classList.toggle('bas-box--items-open', open);
      this.querySelectorAll('[data-items-toggle]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    flashBox() {
      // Briefly surface the box on mobile so the add is visible.
      this.boxEl.classList.add('bas-box--bumped');
      window.setTimeout(() => this.boxEl.classList.remove('bas-box--bumped'), 600);
    }

    /* ---------------------------- carousels ----------------------------- */
    initCarousels() {
      var self = this;
      this.carousels = Array.prototype.slice.call(this.querySelectorAll('[data-carousel]'));
      this.carousels.forEach(function (carousel) {
        var track = carousel.querySelector('[data-carousel-track]');
        if (!track) return;
        track.addEventListener('scroll', function () {
          self.updateCarousel(carousel);
        });
        self.updateCarousel(carousel);
      });
      window.addEventListener('resize', function () {
        self.carousels.forEach(function (c) {
          self.updateCarousel(c);
        });
      });
    }

    /* ---------------------------- category nav -------------------------- */
    // Highlight the pill for whichever category is currently in view.
    initNav() {
      var self = this;
      this.navPills = Array.prototype.slice.call(this.querySelectorAll('[data-nav-pill]'));
      if (!this.navPills.length || typeof IntersectionObserver === 'undefined') return;

      // Fade the nav to white only once it actually sticks to the top.
      var nav = this.querySelector('[data-nav]');
      if (nav) {
        var onNavScroll = function () {
          nav.classList.toggle('is-stuck', nav.getBoundingClientRect().top <= 0);
        };
        window.addEventListener('scroll', onNavScroll, { passive: true });
        window.addEventListener('resize', onNavScroll, { passive: true });
        onNavScroll();
      }

      var sections = this.navPills
        .map(function (p) {
          return document.getElementById(p.dataset.target);
        })
        .filter(Boolean);

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var pill = self.navPills.filter(function (p) {
              return p.dataset.target === entry.target.id;
            })[0];
            if (pill) self.setActivePill(pill);
          });
        },
        // A category is "active" while its top sits in the upper part of the viewport.
        { rootMargin: '-15% 0px -75% 0px', threshold: 0 }
      );
      sections.forEach(function (s) {
        io.observe(s);
      });
    }

    setActivePill(active) {
      (this.navPills || []).forEach(function (p) {
        p.classList.toggle('is-active', p === active);
      });
      // Center the active pill within the nav's own horizontal scroller only
      // (never scrolls the page).
      var scroller = this.querySelector('.bas-nav__inner');
      if (scroller && active) {
        var target = active.offsetLeft - (scroller.clientWidth - active.offsetWidth) / 2;
        scroller.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
    }

    // Toggle overflow (arrow visibility) + prev/next disabled states.
    updateCarousel(carousel) {
      var track = carousel.querySelector('[data-carousel-track]');
      if (!track) return;
      var maxScroll = track.scrollWidth - track.clientWidth;
      carousel.dataset.overflow = maxScroll > 1 ? 'true' : 'false';

      var prev = carousel.querySelector('[data-carousel-prev]');
      var next = carousel.querySelector('[data-carousel-next]');
      if (prev) prev.classList.toggle('is-disabled', track.scrollLeft <= 0);
      if (next) next.classList.toggle('is-disabled', track.scrollLeft >= maxScroll - 1);
    }

    scrollCarousel(carousel, dir) {
      if (!carousel) return;
      var track = carousel.querySelector('[data-carousel-track]');
      var card = track && track.querySelector('.bas-card');
      if (!track || !card) return;
      var step = (card.offsetWidth + 16) * 2; // two cards + gap
      track.scrollLeft += dir * step;
    }

    /* --------------------------- rendering ------------------------------ */
    // Update every card's displayed price to the selected frequency.
    renderCardPrices() {
      var self = this;
      this.querySelectorAll('.bas-card').forEach(function (card) {
        var product = self.products[parseInt(card.dataset.productId, 10)];
        if (!product) return;
        var variants = product.variants || [];
        var variant = variants.find(function (v) { return v.available; }) || variants[0];
        var plan = self.resolvePlan(variant);
        if (!plan) return;
        var priceEl = card.querySelector('[data-price]');
        var compareEl = card.querySelector('[data-compare]');
        if (priceEl) priceEl.textContent = self.money(plan.price);
        if (compareEl) {
          if (plan.compare_at_price > plan.price) {
            compareEl.textContent = self.money(plan.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
      });
    }

    render() {
      var self = this;
      var ids = Object.keys(this.state.items);
      var total = 0;
      var compareTotal = 0;

      this.itemsEl.innerHTML = '';

      ids.forEach(function (variantId) {
        var item = self.state.items[variantId];
        var variant = self.variants[variantId];
        if (!variant) return;
        var plan = self.resolvePlan(variant);
        if (!plan) return;

        var lineTotal = plan.price * item.qty;
        var lineCompare = (plan.compare_at_price > plan.price ? plan.compare_at_price : plan.price) * item.qty;
        total += lineTotal;
        compareTotal += lineCompare;

        self.itemsEl.appendChild(self.renderLine(variant, item, plan));
      });

      // An empty box hides the whole bar on mobile (CSS) and disables checkout.
      // Closing it too means the next add always reopens to the cart view.
      var isEmpty = ids.length === 0;
      this.boxEl.classList.toggle('bas-box--empty', isEmpty);
      if (isEmpty) this.setCartOpen(false);
      this.checkoutBtn.disabled = isEmpty;
      if (this.checkoutNoProtectionBtn) this.checkoutNoProtectionBtn.disabled = isEmpty;

      // Cart-button badges (both instances) carry the total unit count.
      var count = 0;
      ids.forEach(function (variantId) {
        count += self.state.items[variantId].qty;
      });
      this.cartCountEls.forEach(function (el) {
        el.textContent = count;
      });

      // Totals.
      this.totalPriceEl.textContent = this.money(total);
      if (compareTotal > total) {
        this.totalCompareEl.textContent = this.money(compareTotal);
        this.totalCompareEl.hidden = false;
      } else {
        this.totalCompareEl.hidden = true;
      }
      if (this.mobileTotalEl) this.mobileTotalEl.textContent = this.money(total);
      if (this.checkoutPriceEl) {
        this.checkoutPriceEl.textContent = '- ' + this.money(total);
      }

      this.renderShipping(total);
    }

    renderLine(variant, item, plan) {
      var el = document.createElement('div');
      el.className = 'bas-line';
      el.dataset.variant = variant.id;

      var name = variant.productTitle;
      if (variant.title && variant.title !== 'Default Title') {
        name += ' — ' + variant.title;
      }

      var onSale = plan.compare_at_price > plan.price;

      el.innerHTML =
        '<img class="bas-line__image" src="' + this.esc(variant.productImage) + '" alt="" width="56" height="56" loading="lazy">' +
        '<div class="bas-line__info">' +
          '<p class="bas-line__title">' + this.esc(name) + '</p>' +
          '<div class="bas-line__controls">' +
            '<div class="bas-line__qty">' +
              '<button type="button" class="bas-line__step" data-line-dec data-variant="' + variant.id + '" aria-label="Decrease quantity">&minus;</button>' +
              '<span class="bas-line__count">' + item.qty + '</span>' +
              '<button type="button" class="bas-line__step" data-line-inc data-variant="' + variant.id + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="bas-line__remove" data-line-remove data-variant="' + variant.id + '" aria-label="Remove">' +
              REMOVE_ICON +
            '</button>' +
          '</div>' +
        '</div>' +
        '<div class="bas-line__price price">' +
          (onSale ? '<s class="price-item price-item--regular">' + this.money(plan.compare_at_price) + '</s>' : '') +
          '<span class="price-item price-item--sale">' + this.money(plan.price) + '</span>' +
        '</div>';

      return el;
    }

    renderShipping(total) {
      var pct = Math.min(100, Math.round((total / this.threshold) * 100));
      this.shippingFillEl.style.width = pct + '%';
      // Mobile: swaps the collapsed bar between the minimal and the
      // free-shipping-unlocked layouts, in both directions.
      this.boxEl.classList.toggle('bas-box--free-shipping', pct >= 100);
      if (pct >= 100) {
        this.shippingFillEl.classList.add('full');
        // Pop the checkmark once per crossing UP into 100% (the drawer's rule):
        // never on the first paint, and re-armed whenever the box drops below.
        if (this.shippingWasFull === false) this.celebrateShipping();
        this.shippingWasFull = true;
        this.shippingMsgEl.innerHTML =
          "Congrats! You've unlocked <span class=\"free-shipping-message__highlight\">FREE SHIPPING!</span>";
      } else {
        this.shippingWasFull = false;
        this.cancelShippingCelebrate(); // dropping below cancels a pending pop
        this.shippingFillEl.classList.remove('full');
        var needed = this.threshold - total;
        this.shippingMsgEl.innerHTML =
          'You are ' + this.money(needed) +
          ' away from <span class="free-shipping-message__highlight">FREE SHIPPING!</span>';
      }
    }

    // `.is-celebrating` on the root gates the checkmark pop and hides the message
    // in the CSS (same hook + timings the cart drawer uses on <cart-drawer>):
    // burst at 420ms, once the bar has filled to 100%; text back at 1000ms.
    celebrateShipping() {
      if (
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return; // respect reduced-motion: skip the burst + text hide
      }
      this.cancelShippingCelebrate();
      this.root.classList.add('is-celebrating');
      var bar = this.querySelector('.free-shipping-indicator');
      this.celebrateTimers = [
        setTimeout(function () {
          if (typeof window.AAFreeShippingConfetti === 'function') {
            window.AAFreeShippingConfetti(bar);
          }
        }, 420),
        setTimeout(() => {
          this.root.classList.remove('is-celebrating');
        }, 1000)
      ];
    }

    // If the box drops back below the threshold before the burst fires, cancel
    // it — confetti should only ever play on the way UP, never on the way down.
    cancelShippingCelebrate() {
      while (this.celebrateTimers && this.celebrateTimers.length) {
        clearTimeout(this.celebrateTimers.pop());
      }
      this.root.classList.remove('is-celebrating');
    }

    /* ----------------------------- modal -------------------------------- */
    openModal(productId) {
      var product = this.products[productId] || this.readCardData(productId);
      if (!product) return;

      this.modalVariantId = null;
      this.modalQty = 1;

      var variants = product.variants || [];
      var defaultVariant = variants.find(function (v) {
        return v.available;
      }) || variants[0];
      this.modalVariantId = defaultVariant ? defaultVariant.id : null;

      this.modalProduct = product;
      this.modalBodyEl.innerHTML = this.modalMarkup(product);
      this.movePdpMedia(productId);
      this.modalEl.hidden = false;
      document.body.classList.add('bas-modal-open');
      this.refreshModalPrice();

      // Attempt to (re)load Stamped review badges inside the modal.
      if (window.StampedFn && typeof window.StampedFn.loadBadges === 'function') {
        window.StampedFn.loadBadges();
      }

      var closeBtn = this.modalEl.querySelector('[data-modal-close]');
      if (closeBtn) closeBtn.focus();
    }

    // Move the product's pre-rendered aa-pdp-thumbnails into the modal gallery
    // slot (moving keeps its carousel's live event listeners intact).
    movePdpMedia(productId) {
      this.restorePdpMedia();
      var pool = this.querySelector('[data-pdp-media-pool]');
      var slot = this.modalEl.querySelector('[data-modal-gallery]');
      if (!pool || !slot) return;
      var media = pool.querySelector('[data-pdp-media="' + productId + '"]');
      if (!media) return;
      slot.appendChild(media);
      this.activePdpMedia = media;
    }

    // Return the currently-shown media element to the hidden pool.
    restorePdpMedia() {
      if (!this.activePdpMedia) return;
      var pool = this.querySelector('[data-pdp-media-pool]');
      if (pool) pool.appendChild(this.activePdpMedia);
      this.activePdpMedia = null;
    }

    modalMarkup(product) {
      var self = this;

      // The gallery is filled by moving in the product's pre-rendered
      // aa-pdp-thumbnails component (see openModal / restorePdpMedia).
      var gallery = '<div class="bas-modal__gallery" data-modal-gallery></div>';

      var variantPicker = '';
      if (product.has_multiple_variants) {
        variantPicker =
          '<div class="bas-modal__variants" data-modal-variants>' +
            product.variants.map(function (v) {
              var disabled = v.available ? '' : ' disabled';
              var active = v.id === self.modalVariantId ? ' is-active' : '';
              return '<button type="button" class="bas-modal__variant' + active +
                '" data-modal-variant="' + v.id + '"' + disabled + '>' + self.esc(v.title) + '</button>';
            }).join('') +
          '</div>';
      }

      return (
        gallery +
        '<div class="bas-modal__main">' +
          '<div class="bas-modal__details">' +
            '<h2 class="bas-modal__title">' + this.esc(product.title) + '</h2>' +
            '<span class="stamped-product-reviews-badge stamped-main-badge" data-id="' + product.id +
              '" data-product-sku="' + this.esc(product.handle) + '" data-product-title="' + this.esc(product.title) + '"></span>' +
            '<div class="price bas-modal__price" data-modal-price></div>' +
            variantPicker +
            (product.description ? '<div class="bas-modal__description rte">' + product.description + '</div>' : '') +
          '</div>' +
          '<div class="bas-modal__footer">' +
            '<div class="bas-modal__qty">' +
              '<button type="button" class="bas-line__step" data-modal-qty-dec aria-label="Decrease quantity">&minus;</button>' +
              '<span class="bas-line__count" data-modal-qty>1</span>' +
              '<button type="button" class="bas-line__step" data-modal-qty-inc aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="button button--full-width button--primary bas-modal__add" data-modal-add>' +
              'Add to cart' +
            '</button>' +
          '</div>' +
        '</div>'
      );
    }

    modalVariant() {
      var self = this;
      var product = this.modalProduct;
      if (!product) return null;
      return (product.variants || []).find(function (v) {
        return v.id === self.modalVariantId;
      });
    }

    refreshModalPrice() {
      var plan = this.resolvePlan(this.modalVariant());
      var priceEl = this.modalBodyEl.querySelector('[data-modal-price]');
      if (!plan || !priceEl) return;
      var onSale = plan.compare_at_price > plan.price;
      priceEl.innerHTML =
        (onSale ? '<s class="price-item price-item--regular">' + this.money(plan.compare_at_price) + '</s> ' : '') +
        '<span class="price-item price-item--sale">' + this.money(plan.price) + '</span>';
    }

    // Handles clicks inside the modal (routed from the single delegated listener).
    handleModalClick(e) {
      var body = this.modalBodyEl;

      var thumb = e.target.closest('[data-modal-thumb]');
      if (thumb) {
        var mainImg = body.querySelector('[data-modal-image]');
        if (mainImg) mainImg.src = thumb.dataset.modalThumb;
        body.querySelectorAll('[data-modal-thumb]').forEach(function (t) {
          t.classList.toggle('is-active', t === thumb);
        });
        return;
      }

      var vBtn = e.target.closest('[data-modal-variant]');
      if (vBtn && !vBtn.disabled) {
        this.modalVariantId = parseInt(vBtn.dataset.modalVariant, 10);
        body.querySelectorAll('[data-modal-variant]').forEach(function (b) {
          b.classList.toggle('is-active', b === vBtn);
        });
        var variant = this.modalVariant();
        if (variant && variant.image) {
          var mainImg2 = body.querySelector('[data-modal-image]');
          if (mainImg2) mainImg2.src = variant.image;
        }
        this.refreshModalPrice();
        return;
      }

      if (e.target.closest('[data-modal-qty-inc]')) {
        this.modalQty += 1;
        body.querySelector('[data-modal-qty]').textContent = this.modalQty;
        return;
      }
      if (e.target.closest('[data-modal-qty-dec]')) {
        this.modalQty = Math.max(1, this.modalQty - 1);
        body.querySelector('[data-modal-qty]').textContent = this.modalQty;
        return;
      }

      if (e.target.closest('[data-modal-add]')) {
        if (this.modalVariantId) this.addVariant(this.modalVariantId, this.modalQty);
        this.closeModal();
      }
    }

    closeModal() {
      this.restorePdpMedia();
      this.modalEl.hidden = true;
      this.modalProduct = null;
      document.body.classList.remove('bas-modal-open');
    }

    /* ---------------------------- checkout ------------------------------ */
    checkout(withProtection, btn) {
      var self = this;
      var ids = Object.keys(this.state.items);
      if (!ids.length) return;

      var items = ids.map(function (variantId) {
        var item = self.state.items[variantId];
        var variant = self.variants[variantId];
        var plan = self.resolvePlan(variant);
        var line = { id: parseInt(variantId, 10), quantity: item.qty };
        if (plan && plan.id) line.selling_plan = plan.id;
        return line;
      });

      if (withProtection && this.protectionVariant) {
        items.unshift({ id: parseInt(this.protectionVariant, 10), quantity: 1 });
      }
      console.log(LOG, 'checkout — withProtection:', !!withProtection, 'items:', items);

      btn = btn || this.checkoutBtn;
      this.setLoading(btn, true);
      this.hideError();

      // Replace: clear the real cart, then add the box, then go to checkout.
      fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items }),
          });
        })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (d) { throw new Error(d.description || 'Could not add items.'); });
          window.location.href = '/checkout';
        })
        .catch(function (err) {
          self.setLoading(btn, false);
          self.showError(err.message || 'Something went wrong. Please try again.');
        });
    }

    setLoading(btn, loading) {
      if (!btn) return;
      btn.classList.toggle('bas-loading', loading);
      btn.disabled = loading;
    }

    showError(msg) {
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    hideError() {
      this.errorEl.hidden = true;
    }

    /* ---------------------------- utilities ----------------------------- */
    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    esc(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  // Initialize every instance on the page. Runs whether the script loads before
  // or after DOMContentLoaded, and guards against double-init per element.
  function initAll() {
    var nodes = document.querySelectorAll('aa-subscription-box');
    console.log(LOG, 'initAll: found', nodes.length, '<aa-subscription-box> element(s)');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i]._basController) {
        try {
          nodes[i]._basController = new SubscriptionBox(nodes[i]);
        } catch (e) {
          console.error(LOG, 'failed to init element', nodes[i], e);
        }
      }
    }
  }

  if (document.readyState === 'loading') {
    console.log(LOG, 'document loading — waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    console.log(LOG, 'document ready — initializing now');
    initAll();
  }

  // Back/forward (bfcache) restore: after a checkout redirect, the browser can
  // restore this page with the checkout button still spinning AND the theme's
  // cart drawer showing its stale pre-checkout state. Reload so both reflect
  // the true current cart. Only fires on an actual bfcache restore.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      console.log(LOG, 'restored from bfcache — reloading to refresh cart + reset UI');
      window.location.reload();
    }
  });
})();
